import type { FieldErrors, FieldValues, Path } from "react-hook-form";

/**
 * Depth-first walk of react-hook-form FieldErrors to find the first `.message`.
 * Handles nested objects, field arrays, and `customInputScopes[0].optionIds` shapes.
 */
export function getFirstNestedErrorMessage(
  errors: FieldErrors | undefined
): string | undefined {
  if (!errors || typeof errors !== "object") return undefined;

  const walk = (node: unknown): string | undefined => {
    if (node == null || typeof node !== "object") return undefined;
    const n = node as Record<string, unknown>;
    if (typeof n.message === "string" && n.message.length > 0) {
      return n.message;
    }
    if (Array.isArray(node)) {
      for (const item of node) {
        const m = walk(item);
        if (m) return m;
      }
      return undefined;
    }
    for (const key of Object.keys(n)) {
      if (key === "ref") continue;
      const m = walk(n[key]);
      if (m) return m;
    }
    return undefined;
  };

  return walk(errors);
}

/**
 * RHF `setFocus` path for the first error leaf (the one carrying `.message`).
 */
export function getFirstErrorFieldPath<T extends FieldValues>(
  errors: FieldErrors<T> | undefined,
  basePath = ""
): Path<T> | undefined {
  if (!errors || typeof errors !== "object") return undefined;

  const walk = (node: unknown, path: string): string | undefined => {
    if (node == null || typeof node !== "object") return undefined;
    const n = node as Record<string, unknown>;

    if (typeof n.message === "string" && n.message.length > 0) {
      return path || undefined;
    }
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        const seg = path ? `${path}.${i}` : String(i);
        const sub = walk(node[i], seg);
        if (sub) return sub;
      }
      return undefined;
    }
    for (const key of Object.keys(n)) {
      if (key === "ref") continue;
      const seg = path ? `${path}.${key}` : key;
      const sub = walk(n[key], seg);
      if (sub) return sub;
    }
    return undefined;
  };

  return walk(errors, basePath) as Path<T> | undefined;
}

/** Text for a custom-input scope cell (RHF + Zod nested errors). */
export function getCustomInputScopeErrorMessage(scopeErr: unknown): string {
  if (!scopeErr || typeof scopeErr !== "object") return "";
  const s = scopeErr as Record<string, unknown>;
  if (typeof s.message === "string" && s.message) return s.message;
  const opt = s.optionIds;
  if (opt && typeof opt === "object" && "message" in opt) {
    const m = (opt as { message?: string }).message;
    if (typeof m === "string" && m) return m;
  }
  return "";
}

export function hasCustomInputScopeError(scopeErr: unknown): boolean {
  return getCustomInputScopeErrorMessage(scopeErr).length > 0;
}
